import {KeyData} from 'notebook/mediocre/selection/SelectionStore';

export interface HistoryState<TState> {
  selectionData: KeyData;
  state: TState; // Varies across usage in Documents/Entries & Protocols
}

export const MAX_STATES = 50;

/**
 * States is implemented as a circular buffer of size MAX_STATES, with entries starting at head, ending at
 * tail inclusive. It must ALWAYS have at least one entry.
 *
 * @states[@walk] is what the user is currently viewing. This might be a placeholder for the current state
 * (_PLACEHOLDER), since we don't care about the current state until we are about to depart from it (with
 * a push state).
 *
 * When we depart from the current state, we might need to fill the placeholder in with the current state
 * (e.g. walk === head and we have a push or undo). Otherwise we're simply exploring tail -> walk -> head.
 */
class HistoryManager<TState = unknown> {
  private _PLACEHOLDER = {} as HistoryState<TState>;
  private states: Array<HistoryState<TState>> = [this._PLACEHOLDER];
  private head = 0;
  private tail = 0;
  private walk = 0;

  public reset(): void {
    this.states = [this._PLACEHOLDER];
    this.head = 0;
    this.tail = 0;
    this.walk = 0;
  }

  /**
   * Exposes the internal circular-buffer state for assertions.
   *
   * @internal Test-only. Production consumers must go through the public undo/redo/push API; the buffer
   * pointers are implementation details and are not part of the supported interface.
   */
  public getInternalStateForTest(): {
    states: Array<HistoryState<TState>>;
    head: number;
    tail: number;
    walk: number;
  } {
    return {states: this.states, head: this.head, tail: this.tail, walk: this.walk};
  }

  /**
   * Simple reference to our previous pointer, with logic to forward by MAX_STATES if we go below 0.
   * @param pointer pointer to step back from
   * @returns new pointer
   */
  private _prev(pointer: number): number {
    return (pointer - 1 + MAX_STATES) % MAX_STATES;
  }

  public canUndo(): boolean {
    return this.walk !== this.tail;
  }

  public canRedo(): boolean {
    return this.walk !== this.head;
  }

  /**
   * Completes an undo with the current state, i.e. walking back our walk
   * field to prev, and replacing the placeholder state if we have to. Guards with
   * canUndo().
   * @param state State we may need to reference for replacement
   * @returns Prev state
   */
  public undo(state: HistoryState<TState>): HistoryState<TState> {
    if (!this.canUndo()) {
      throw new Error('No previous state to walk back to.');
    }
    if (this.states[this.walk] === this._PLACEHOLDER) {
      this.states[this.walk] = state;
    }
    this.walk = this._prev(this.walk);
    return this.states[this.walk];
  }

  /**
   * Completes a redo with the current state, i.e. walking forward our
   * walk field to walk + 1 % MAX_STATES. Guards with canRedo().
   * @returns Next state
   */
  public redo(): HistoryState<TState> {
    if (!this.canRedo()) {
      throw new Error('No future state to walk forward to.');
    }
    this.walk = (this.walk + 1) % MAX_STATES;
    return this.states[this.walk];
  }

  /**
   * Pushes the given state onto the circular buffer, regardless of if we're
   * at the head or not. Increments walk and inserts a placeholder.
   */
  public pushState(state: HistoryState<TState>): void {
    this.states[this.walk] = state;
    this.walk = (this.walk + 1) % MAX_STATES;
    if (this.walk === this.tail) {
      this.tail = (this.tail + 1) % MAX_STATES;
    }
    this.head = this.walk;
    this.states[this.walk] = this._PLACEHOLDER;
  }

  /**
   * Undos the push state we just completed if a given condition is true (@matchFn).
   * @param matchFn Function to check prev state against
   * @returns If we successfully undid the operation
   */
  public undoPushStateIf(matchFn: (state: HistoryState<TState>) => boolean): boolean {
    // Only allow undo if we just pushed.
    if (this.head !== this.walk) {
      return false;
    }
    // Can only undo if there is something to undo to.
    if (this.head === this.tail) {
      return false;
    }
    // Peek at our undo().
    const prev = this._prev(this.walk);
    const prevState = this.states[prev];
    if (!matchFn(prevState)) {
      return false;
    }
    this.walk = prev;
    this.states[this.walk] = this._PLACEHOLDER;
    this.head = this.walk;
    return true;
  }
}

export default HistoryManager;
